/**
 * Desktop Dropdown Components
 *
 * All desktop navigation dropdown menus for the header
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { generateSlug } from "../../../utils/slugUtils";
import { NavigationLink, NavigationDropdown } from "../NavigationStyles";
import NavigationStylesExport from "../NavigationStyles";

// Products Dropdown Component for Desktop
export const ProductsDropdown = ({ productItems, isOpen, setIsOpen }) => {
  const { t } = useTranslation("common");

  // Translation mapping for product items
  const translateProductItem = (item) => {
    const mapping = {
      "TadReamk Eye": t("products.tadreamkEye"),
      "TadReamk Create": t("products.tadreamkCreate"),
      "TadReamk Patent": t("products.tadreamkPatent"),
      "TadReamk Surveillance": t("products.tadreamkSurveillance"),
      "Trademark Trading": t("products.trademarkTrading"),
      "Business Card Manager": t("products.businessCardManager"),
    };
    return mapping[item] || item;
  };

  return (
    <div className={NavigationStylesExport.HEADER_STYLES.dropdownContainer}>
      <NavigationLink
        to="/products"
        variant="desktop-link"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {t("navigation.products")}
      </NavigationLink>

      {/* Desktop Dropdown Menu */}
      <NavigationDropdown
        isOpen={isOpen}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {productItems.map((item, index) => (
          <NavigationLink
            key={index}
            to={`/products/${generateSlug(item)}`}
            variant="desktop-dropdown"
            onClick={() => setIsOpen(false)}
          >
            {translateProductItem(item)}
          </NavigationLink>
        ))}
      </NavigationDropdown>
    </div>
  );
};

// Magazine Dropdown Component for Desktop
export const MagazineDropdown = ({ magazineItems, isOpen, setIsOpen }) => {
  const { t } = useTranslation("common");

  const getItemPath = (item) => {
    switch (item) {
      case "TadReamk AI":
        return "/magazine/ai";
      case "TadReamk Design":
        return "/magazine/design";
      case "IP Law":
        return "/magazine/law";
      case "TadReamk-AIGC":
        return "/magazine/aigc";
      case "TadReamk-ERP":
        return "/magazine/erp";
      default:
        return `/magazine/${generateSlug(item)}`;
    }
  };

  // Translation mapping for magazine items
  const translateMagazineItem = (item) => {
    const mapping = {
      "IP Law": t("magazine.ipLaw"),
      "TadReamk AI": t("magazine.tadreamkAI"),
      "TadReamk Design": t("magazine.tadreamkDesign"),
      "TadReamk-AIGC": t("magazine.tadreamkAIGC"),
      "TadReamk-ERP": t("magazine.tadreamkERP"),
    };
    return mapping[item] || item;
  };

  return (
    <div className={NavigationStylesExport.HEADER_STYLES.dropdownContainer}>
      <NavigationLink
        to="/magazine"
        variant="desktop-link"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {t("navigation.magazine")}
      </NavigationLink>

      {/* Desktop Dropdown Menu */}
      <NavigationDropdown
        isOpen={isOpen}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {magazineItems.map((item, index) => (
          <NavigationLink
            key={index}
            to={getItemPath(item)}
            variant="desktop-dropdown"
            onClick={() => setIsOpen(false)}
          >
            {translateMagazineItem(item)}
          </NavigationLink>
        ))}
      </NavigationDropdown>
    </div>
  );
};

// Business Dropdown Component for Desktop
export const BusinessDropdown = ({ businessItems, isOpen, setIsOpen }) => {
  const { t } = useTranslation("common");

  const getItemPath = (item) => {
    switch (item) {
      case "Overview":
        return "/business/overview";
      case "API Documentation":
        return "/business/api";
      case "Contact Sales":
        return "/business/contact";
      case "Pricing":
        return "/business/pricing";
      default:
        return `/business/${generateSlug(item)}`;
    }
  };

  // Translation mapping for business items
  const translateBusinessItem = (item) => {
    const mapping = {
      Overview: t("business.overview"),
      "API Documentation": t("business.apiDocumentation"),
      "Contact Sales": t("business.contactSales"),
      Pricing: t("business.pricing"),
    };
    return mapping[item] || item;
  };

  return (
    <div className={NavigationStylesExport.HEADER_STYLES.dropdownContainer}>
      <NavigationLink
        to="/business/overview"
        variant="desktop-link"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {t("navigation.business")}
      </NavigationLink>

      {/* Desktop Dropdown Menu */}
      <NavigationDropdown
        isOpen={isOpen}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {businessItems.map((item, index) => (
          <NavigationLink
            key={index}
            to={getItemPath(item)}
            variant="desktop-dropdown"
            onClick={() => setIsOpen(false)}
          >
            {translateBusinessItem(item)}
          </NavigationLink>
        ))}
      </NavigationDropdown>
    </div>
  );
};

// Company Dropdown Component for Desktop
export const CompanyDropdown = ({ companyItems, isOpen, setIsOpen }) => {
  const { t } = useTranslation("common");

  const getItemPath = (item) => {
    switch (item) {
      case "About Us":
        return "/about";
      case "Careers":
        return "/careers";
      case "News":
        return "/news";
      default:
        return `/${generateSlug(item)}`;
    }
  };

  // Translation mapping for company items
  const translateCompanyItem = (item) => {
    const mapping = {
      "About Us": t("companyLinks.aboutUs"),
      Careers: t("companyLinks.careers"),
    };
    return mapping[item] || item;
  };

  return (
    <div className={NavigationStylesExport.HEADER_STYLES.dropdownContainer}>
      <NavigationLink
        to="/about"
        variant="desktop-link"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {t("navigation.company")}
      </NavigationLink>

      {/* Desktop Dropdown Menu */}
      <NavigationDropdown
        isOpen={isOpen}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {companyItems.map((item, index) => (
          <NavigationLink
            key={index}
            to={getItemPath(item)}
            variant="desktop-dropdown"
            onClick={() => setIsOpen(false)}
          >
            {translateCompanyItem(item)}
          </NavigationLink>
        ))}
      </NavigationDropdown>
    </div>
  );
};

// AI Services Dropdown Component for Desktop
export const AIServicesDropdown = ({ aiServicesItems, isOpen, setIsOpen }) => {
  const { t } = useTranslation("common");

  // Translation mapping for AI services items
  const translateAIServiceItem = (item) => {
    const mapping = {
      "Lace Material": t("products.laceMaterial"),
    };
    return mapping[item] || item;
  };

  return (
    <div className={NavigationStylesExport.HEADER_STYLES.dropdownContainer}>
      <NavigationLink
        to="/ai-services"
        variant="desktop-link"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {t("navigation.aiServices")}
      </NavigationLink>

      {/* Desktop Dropdown Menu */}
      <NavigationDropdown
        isOpen={isOpen}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {aiServicesItems.map((item, index) => (
          <NavigationLink
            key={index}
            to={`/ai-services/${generateSlug(item)}`}
            variant="desktop-dropdown"
            onClick={() => setIsOpen(false)}
          >
            {translateAIServiceItem(item)}
          </NavigationLink>
        ))}
      </NavigationDropdown>
    </div>
  );
};
